"use client";

import { c as _c } from "react/compiler-runtime";
import { Children, useEffect, useRef, useState } from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "@/hooks/useMediaQuery";
interface UploadProgressMeterProps {
  progress: number;
  savedCount: number;
  totalCount: number;
  /** Every remaining file is sent and waiting for the server to confirm it. */
  isFinishing: boolean;
}
const COUNT_UP_DURATION_MS = 450;

// Counts up towards the real value but never past it, and drops immediately
// when progress goes backwards (a retry restarts a file).
function useRisingNumber(target, isAnimated) {
  const $ = _c(4);
  const [shown, setShown] = useState(target);
  const shownRef = useRef(target);
  let t0;
  let t1;
  if ($[0] !== isAnimated || $[1] !== target) {
    t0 = () => {
      const from = shownRef.current;
      if (!isAnimated || target <= from) {
        shownRef.current = target;
        setShown(target);
        return;
      }
      let frame = 0;
      const startedAt = performance.now();
      const tick = now => {
        const elapsed = Math.min(1, (now - startedAt) / COUNT_UP_DURATION_MS);
        const eased = 1 - (1 - elapsed) ** 3;
        const value = Math.floor(from + (target - from) * eased);
        shownRef.current = value;
        setShown(value);
        if (elapsed < 1) {
          frame = requestAnimationFrame(tick);
        }
      };
      frame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frame);
    };
    t1 = [target, isAnimated];
    $[0] = isAnimated;
    $[1] = target;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return shown;
}
export default function UploadProgressMeter(t0) {
  const $ = _c(36);
  const {
    progress,
    savedCount,
    totalCount,
    isFinishing
  } = t0;
  const t = useTranslations("Upload.UploadButton");
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  let t1;
  if ($[0] !== progress) {
    t1 = Math.round(progress);
    $[0] = progress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const clampedProgress = Math.min(100, Math.max(0, t1));
  const shownProgress = useRisingNumber(clampedProgress, !prefersReducedMotion);
  let t2;
  if ($[2] !== t) {
    t2 = t("UploadModal.title");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== clampedProgress) {
    t3 = clampedProgress === 0 && <span className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/90 to-transparent motion-safe:animate-upload-sheen motion-reduce:hidden" />;
    $[4] = clampedProgress;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `translateX(-${100 - clampedProgress}%)`;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      transform: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent motion-safe:animate-upload-sheen motion-reduce:hidden" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <ProgressPrimitive.Indicator className="relative h-full w-full overflow-hidden rounded-full bg-gradient-to-r from-[#ffd08a] via-gg-orange-500 to-[#e8892b] transition-transform duration-500 ease-out" style={t5}>{t6}</ProgressPrimitive.Indicator>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== clampedProgress || $[12] !== t2 || $[13] !== t3 || $[14] !== t7) {
    t8 = <ProgressPrimitive.Root value={clampedProgress} aria-label={t2} className="relative h-3 w-full overflow-hidden rounded-full bg-[#ece3d6]">{t3}{t7}</ProgressPrimitive.Root>;
    $[11] = clampedProgress;
    $[12] = t2;
    $[13] = t3;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== isFinishing || $[17] !== shownProgress || $[18] !== t) {
    t9 = isFinishing ? t("UploadModal.finishing") : `${shownProgress}%`;
    $[16] = isFinishing;
    $[17] = shownProgress;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <span className="font-semibold tabular-nums text-[#3b332b]">{t9}</span>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== savedCount || $[23] !== t || $[24] !== totalCount) {
    let t12;
    if ($[26] !== savedCount) {
      t12 = chunks => <span key={savedCount} translate="no" className="inline-block font-semibold tabular-nums text-[#3b332b] motion-safe:animate-upload-count-bump">{chunks}</span>;
      $[26] = savedCount;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    t11 = Children.map(t.rich("UploadModal.savedCount", {
      completedCount: savedCount,
      totalCount,
      count: t12
    }), _temp);
    $[22] = savedCount;
    $[23] = t;
    $[24] = totalCount;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = <span className="text-right text-[#71675d]">{t11}</span>;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t12) {
    t13 = <div className="mt-2 flex items-baseline justify-between gap-3 text-sm">{t10}{t12}</div>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13 || $[34] !== t8) {
    t14 = <div className="mt-4">{t8}{t13}</div>;
    $[33] = t13;
    $[34] = t8;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}
function _temp(chunk) {
  return typeof chunk === "string" ? <span>{chunk}</span> : chunk;
}